import React, { useState, useEffect } from 'react';
import { MdVisibility, MdCheckCircle, MdCancel, MdSearch, MdEdit, MdInfo, MdHistory, MdRestore, MdPerson, MdAccountBalance, MdSchool, MdLocationOn, MdSecurity, MdLocalPhone } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';

const MyRegistrations = () => {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);

    useEffect(() => {
        fetchMyForms();
    }, []);

    const fetchMyForms = async () => {
        try {
            const res = await api.get('/kushal-yuva/my-forms');
            setForms(res.data);
        } catch (error) {
            console.error("Failed to fetch registrations");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        if (!window.confirm(`Are you sure you want to change status to ${status}? This action will lock the status.`)) return;
        try {
            await api.put(`/kushal-yuva/forms/${id}/status`, { status });
            fetchMyForms();
            alert("Status updated and locked!");
        } catch (error) {
            alert(error.response?.data?.message || "Failed to update status");
        }
    };

    const filteredForms = forms.filter(f =>
        f.applicant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.mobile_number.includes(searchTerm)
    );

    const Th = ({ children, center }) => <th className={`px-3 py-3 ${center ? 'text-center' : 'text-left'} text-[11px] font-extrabold text-gray-600 uppercase tracking-tighter whitespace-nowrap`}>{children}</th>;
    const Td = ({ children, center }) => <td className={`px-3 py-3 whitespace-nowrap text-[11px] text-gray-700 border-b border-gray-100 align-middle ${center ? 'text-center' : ''}`}>{children}</td>;

    return (
        <div className="space-y-4">
            {/* Header */}
            {/* Header Strip */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-500 p-2 px-3 rounded-lg flex justify-between items-center shadow-md">
                <div className="flex items-center gap-1.5 text-white min-w-0">
                    <MdRestore size={16} className="shrink-0" />
                    <h2 className="font-extrabold uppercase tracking-tight text-[10px] whitespace-nowrap overflow-hidden text-ellipsis">My Registrations</h2>
                </div>
                <div className="relative shrink-0">
                    <MdSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-white/60" size={12} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-white/10 border border-white/10 text-white rounded-md pl-6 pr-2 py-1 text-[10px] focus:outline-none focus:bg-white/20 placeholder:text-white/40 w-24 md:w-40 transition-all outline-none"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <Th>Applicant Name</Th>
                                <Th>Contact</Th>
                                <Th>Date</Th>
                                <Th>Cat.</Th>
                                <Th>Registered By</Th>
                                <Th center>Status</Th>
                                <Th center>Action</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="p-8 text-center text-gray-400">Loading...</td></tr>
                            ) : filteredForms.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-gray-400">No registrations found.</td></tr>
                            ) : filteredForms.map(form => (
                                <tr key={form.id} className="hover:bg-gray-50/50 transition-colors">
                                    <Td>
                                        <div className="flex items-center gap-1.5">
                                            {form.student_photo ? (
                                                <img src={form.student_photo} alt="Student" className="w-8 h-8 rounded-full object-cover border-2 border-blue-50 shadow-sm" />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 border border-gray-100">
                                                    <MdPerson size={14} />
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="font-bold text-gray-800 leading-tight truncate max-w-[100px]">{form.applicant_name}</div>
                                                <div className="text-[9px] text-gray-400 truncate max-w-[100px]">F: {form.father_husband_name}</div>
                                            </div>
                                        </div>
                                    </Td>
                                    <Td>
                                        <div className="flex items-center gap-1 font-bold text-gray-700 text-[10px]"><MdLocalPhone size={10} className="text-blue-500" /> {form.mobile_number}</div>
                                    </Td>
                                    <Td><span className="text-[10px] tabular-nums">{new Date(form.created_at).toLocaleDateString()}</span></Td>
                                    <Td>
                                        <div className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-[10px] font-bold inline-block uppercase italic tracking-wider border border-blue-100 uppercase">
                                            {form.category}
                                        </div>
                                    </Td>
                                    <Td>
                                        <div className="font-bold text-blue-900 capitalize truncate max-w-[100px] leading-tight text-[10px]">{form.registered_by_name || 'Staff'}</div>
                                    </Td>
                                    <Td center>
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${form.status === 'approved' ? 'bg-green-100 text-green-700' :
                                            form.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {form.status === 'approved' ? 'Confirmed' : (form.status === 'rejected' ? 'Rejected' : 'Pending')}
                                        </span>
                                    </Td>
                                    <Td center>
                                        <div className="flex gap-2 justify-center">
                                            {/* Status Selector - Only if Pending */}
                                            {form.status === 'pending' ? (
                                                <select
                                                    onChange={(e) => updateStatus(form.id, e.target.value)}
                                                    className="text-xs border rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    defaultValue="pending"
                                                >
                                                    <option value="pending" disabled>Change Status...</option>
                                                    <option value="approved">Confirm</option>
                                                    <option value="rejected">Reject</option>
                                                </select>
                                            ) : (
                                                <span className="text-[10px] text-gray-400 font-medium italic flex items-center gap-1">
                                                    <MdCheckCircle size={12} /> Locked
                                                </span>
                                            )}

                                            <button
                                                onClick={() => setSelectedForm(form)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                title="View Details"
                                            >
                                                <MdVisibility size={18} />
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Details Modal */}
            <AnimatePresence>
                {selectedForm && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <div className="bg-blue-900 p-4 text-white flex justify-between items-center">
                                <h3 className="font-bold flex items-center gap-2"><MdInfo /> Registration Details</h3>
                                <button onClick={() => setSelectedForm(null)} className="hover:bg-white/10 p-1 rounded-full"><MdCancel size={24} /></button>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4 bg-gray-50/50">
                                {/* Personal */}
                                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm space-y-3">
                                    <h4 className="text-blue-900 font-bold border-b pb-2 flex items-center gap-2"><MdPerson /> Personal Information</h4>

                                    {/* Student Photo - Round Circle */}
                                    {selectedForm.student_photo && (
                                        <div className="flex justify-center mb-6 pt-2">
                                            <div className="relative group">
                                                <div className="w-32 h-32 rounded-full ring-4 ring-blue-50 ring-offset-2 overflow-hidden shadow-xl">
                                                    <img
                                                        src={selectedForm.student_photo}
                                                        alt="Student"
                                                        className="w-full h-full object-cover transform transition-transform group-hover:scale-110"
                                                    />
                                                </div>
                                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[8px] px-3 py-1 rounded-full font-bold shadow-md uppercase whitespace-nowrap border-2 border-white">Live Profile</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <FormField label="Full Name" value={selectedForm.applicant_name} />
                                        <FormField label="Father/Husband" value={selectedForm.father_husband_name} />
                                        <FormField label="Gender" value={selectedForm.gender} />
                                        <FormField label="DOB" value={selectedForm.dob} />
                                        <FormField label="Aadhar" value={selectedForm.aadhar_number || selectedForm.eid_number} />
                                        <FormField label="Category" value={selectedForm.category} />
                                        <div className="col-span-2">
                                            <FormField label="Address" value={selectedForm.residential_address} />
                                        </div>
                                    </div>
                                </div>

                                {/* Bank & Contact */}
                                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm space-y-3">
                                    <h4 className="text-blue-900 font-bold border-b pb-2 flex items-center gap-2"><MdAccountBalance /> Bank & Contact Details</h4>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <FormField label="Bank" value={selectedForm.bank_name} icon={<MdAccountBalance className="text-gray-300" />} />
                                        <FormField label="Branch" value={selectedForm.bank_branch} />
                                        <FormField label="Account Number" value={selectedForm.account_number} />
                                        <FormField label="IFSC Code" value={selectedForm.ifsc_code} />
                                        <FormField label="Phone" value={selectedForm.mobile_number} />
                                        <FormField label="Email" value={selectedForm.email_id} />
                                    </div>
                                </div>

                                {/* Education */}
                                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm space-y-3">
                                    <h4 className="text-blue-900 font-bold border-b pb-2 flex items-center gap-2"><MdSchool /> Education (12th)</h4>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <FormField label="Institution" value={selectedForm.inst_name} />
                                        <FormField label="Board" value={selectedForm.board_name} />
                                        <FormField label="Roll No" value={selectedForm.roll_no} />
                                        <FormField label="Passing Year" value={selectedForm.passing_year} />
                                    </div>
                                </div>

                                {/* Tracking */}
                                <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 shadow-sm space-y-3">
                                    <h4 className="text-blue-900 font-bold border-b pb-2 flex items-center gap-2"><MdSecurity /> Submission Tracking</h4>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <FormField label="Registered By" value={selectedForm.registered_by_name || 'Staff'} />
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Live Location</span>
                                            {selectedForm.latitude ? (
                                                <a
                                                    href={`https://www.google.com/maps?q=${selectedForm.latitude},${selectedForm.longitude}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline mt-1"
                                                >
                                                    <MdLocationOn /> View on Map
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-400 mt-1 italic">No location data</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FormField = ({ label, value, icon }) => (
    <div>
        <span className="block text-[9px] text-gray-400 uppercase font-bold tracking-tight mb-0.5">{label}</span>
        <div className="bg-gray-50 border border-gray-100 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-800 flex items-center gap-2 min-h-[32px] overflow-hidden truncate">
            {icon} <span className="truncate">{value || 'N/A'}</span>
        </div>
    </div>
);

export default MyRegistrations;
